import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import TabNavigation from '../components/TabNavigation';
import { Bell, Trash2, Check, CheckCheck, Clock, Filter } from 'lucide-react';
import { formatTimeAgo } from '../utils/dateUtils';

const PAGE_SIZES = [10, 25, 50, 100];
const pagerBtn = 'rounded-lg border border-gray-300 px-2.5 py-1 text-gray-700 '
  + 'hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40';

const Notifications = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);       // initial load only
  const [refreshing, setRefreshing] = useState(false); // any request in flight
  const requestSeqRef = useRef(0);
  const [filter, setFilter] = useState('all'); // all, unread, read
  const [moduleFilter, setModuleFilter] = useState('all');

  // Paging lives on the SERVER. The page used to ask for `limit=100` and slice
  // nothing: a user with 271 notifications saw the newest 100, the older 171
  // were unreachable, and the footer said "Showing 100 of 100" — which is not
  // a rounding error, it is the wrong number.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [total, setTotal] = useState(0);        // rows matching the filters
  const [unreadCount, setUnreadCount] = useState(0);
  const [modules, setModules] = useState([]);   // every module, not just this page

  // The filters go to the server with the page. Filtering client-side after
  // paging would have filtered only the 25 rows on screen — "Unread" would
  // show the unread ones of page 1 and an empty page 2.
  //
  // Stale-response guard: every call takes a sequence number and only the
  // newest one may write state. Without it, flicking Unread -> Read -> All
  // quickly let a slow earlier response land last and overwrite the list
  // with rows for a filter that is no longer selected.
  //
  // `loading` is the FIRST load only (nothing to show yet). A refetch after a
  // mark-read / delete / filter change keeps the current list on screen and
  // just sets `refreshing`, instead of swapping the list for a full spinner.
  const fetchNotifications = useCallback(async () => {
    const requestId = ++requestSeqRef.current;
    setRefreshing(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        limit: String(pageSize),
        offset: String((page - 1) * pageSize),
      });
      if (filter === 'unread') params.set('read', 'false');
      if (filter === 'read') params.set('read', 'true');
      if (moduleFilter !== 'all') params.set('module', moduleFilter);

      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/notifications?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (requestId !== requestSeqRef.current) return; // superseded
      const data = response.data || {};
      setNotifications(data.notifications || []);
      setTotal(data.total || 0);
      setUnreadCount(data.unread || 0);
      setModules(data.modules || []);
    } catch (error) {
      if (requestId !== requestSeqRef.current) return; // superseded
      console.error('Error fetching notifications:', error);
      setNotifications([]);
      setTotal(0);
    } finally {
      if (requestId === requestSeqRef.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [page, pageSize, filter, moduleFilter]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const firstRow = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const lastRow = Math.min(page * pageSize, total);

  // Changing a filter re-queries from the start. Staying on page 6 of a set
  // that now has two pages shows an empty screen and looks like no results.
  const changeFilter = (setter) => (value) => { setter(value); setPage(1); };

  // A page that empties itself — the last unread item marked read while
  // "Unread" is selected — would otherwise sit on a page past the end.
  useEffect(() => {
    if (!loading && !refreshing && page > totalPages) setPage(totalPages);
  }, [loading, refreshing, page, totalPages]);

  const markAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/notifications/${notificationId}/mark-read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Optimistic for instant feedback, then resync: the unread count and
      // the total are the server's to own now, and under the "Unread" filter
      // this row no longer belongs on the page at all.
      setNotifications((prev) => prev.map(n =>
        n.id === notificationId ? { ...n, is_read: true } : n
      ));
      setUnreadCount((c) => Math.max(0, c - 1));
      fetchNotifications();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/notifications/mark-all-read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setNotifications((prev) => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
      fetchNotifications();
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/notifications/${notificationId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // A delete changes the total, so the page after it shifts by one — the
      // refetch is what stops a row being skipped on the next page.
      setNotifications((prev) => prev.filter(n => n.id !== notificationId));
      fetchNotifications();
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const getModuleRoute = (module, referenceId) => {
    const routes = {
      'safety': `/incidents/${referenceId}`,
      'incidents': `/incidents/${referenceId}`,
      'capa': `/capa/${referenceId}`,
      'ptw': `/ptw/${referenceId}`,
      'documents': `/documents/${referenceId}`,
      'meetings': `/meetings/${referenceId}`,
      'audits': `/audits/detail/${referenceId}`,
      'hira': `/hira/${referenceId}`,
      'training': `/training/records?highlight=${referenceId}`,
    };
    return routes[module] || null;
  };

  const handleNotificationClick = (notification) => {
    if (!notification.is_read) {
      markAsRead(notification.id);
    }

    if (notification.reference_url) {
      navigate(notification.reference_url);
    } else if (notification.reference_id && notification.module) {
      const route = getModuleRoute(notification.module, notification.reference_id);
      if (route) {
        navigate(route);
      }
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800 border-red-300';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'normal': return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'low': return 'bg-gray-100 text-gray-800 border-gray-300';
      default: return 'bg-blue-100 text-blue-800 border-blue-300';
    }
  };

  const getModuleIcon = (module) => {
    const icons = {
      'documents': '📄',
      'ptw': '🔒',
      'meetings': '👥',
      'capa': '🔧',
      'hira': '⚠️',
      'safety': '🛡️',
      'training': '📚',
      'audits': '✅',
      'ssem': '📊'
    };
    return icons[module] || '📌';
  };


  // No client-side filtering left: `filter` and `moduleFilter` are sent to the
  // server, so what came back IS the filtered set. Re-filtering here would
  // silently drop rows the server already counted in `total`, and the footer
  // would stop matching the list.
  const filteredNotifications = notifications;

  return (
    <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <Bell className="w-8 h-8 text-blue-600" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Notifications</h1>
                <p className="text-sm text-gray-600">
                  {unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <CheckCheck className="w-4 h-4" />
                <span>Mark All Read</span>
              </button>
            )}
          </div>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center space-x-2">
              <Filter className="w-4 h-4 text-gray-500" />
              <span className="text-sm text-gray-600 font-semibold">Status:</span>
              <select
                value={filter}
                onChange={(e) => changeFilter(setFilter)(e.target.value)}
                className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All</option>
                <option value="unread">Unread</option>
                <option value="read">Read</option>
              </select>
            </div>

            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600 font-semibold">Module:</span>
              <select
                value={moduleFilter}
                onChange={(e) => changeFilter(setModuleFilter)(e.target.value)}
                className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Modules</option>
                {modules.map(module => (
                  <option key={module} value={module}>
                    {getModuleIcon(module)} {module.toUpperCase()}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Notifications List */}
        {loading ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading notifications...</p>
          </div>
        ) : filteredNotifications.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <Bell className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No notifications</h3>
            <p className="text-gray-600">
              {filter === 'unread' 
                ? "You're all caught up!"
                : moduleFilter !== 'all'
                ? `No notifications from ${moduleFilter} module`
                : "You don't have any notifications yet"}
            </p>
          </div>
        ) : (
          <div
            className={`space-y-2 transition-opacity ${refreshing ? 'opacity-60' : ''}`}
            aria-busy={refreshing}
          >
            {filteredNotifications.map((notification) => (
              <div
                key={notification.id}
                onClick={() => handleNotificationClick(notification)}
                className={`bg-white rounded-lg shadow-sm border border-gray-200 p-4 cursor-pointer transition-all hover:shadow-md ${
                  !notification.is_read ? 'bg-blue-50 border-blue-200' : ''
                }`}
              >
                <div className="flex items-start space-x-4">
                  {/* Module Icon */}
                  <div className="text-3xl flex-shrink-0">{getModuleIcon(notification.module)}</div>
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className={`text-base font-semibold ${!notification.is_read ? 'text-gray-900' : 'text-gray-700'}`}>
                          {notification.title}
                        </h3>
                        <p className="text-sm text-gray-600 mt-1">{notification.message}</p>
                        
                        {/* Metadata */}
                        <div className="flex items-center flex-wrap gap-3 mt-3">
                          {/* Priority Badge */}
                          {notification.priority !== 'normal' && (
                            <span className={`inline-flex items-center px-2.5 py-0.5 text-xs font-semibold rounded border ${getPriorityColor(notification.priority)}`}>
                              {notification.priority.toUpperCase()}
                            </span>
                          )}
                          
                          {/* Module Badge */}
                          <span className="inline-flex items-center px-2.5 py-0.5 text-xs font-semibold rounded bg-gray-100 text-gray-700 border border-gray-300">
                            {notification.module.toUpperCase()}
                          </span>
                          
                          {/* Time */}
                          <div className="flex items-center space-x-1 text-xs text-gray-500">
                            <Clock className="w-3 h-3" />
                            <span>{formatTimeAgo(notification.created_at)}</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex items-center space-x-2 ml-4 flex-shrink-0">
                        {!notification.is_read && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              markAsRead(notification.id);
                            }}
                            className="p-2 hover:bg-blue-100 rounded-lg transition-colors"
                            title="Mark as read"
                          >
                            <Check className="w-4 h-4 text-blue-600" />
                          </button>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteNotification(notification.id);
                          }}
                          className="p-2 hover:bg-red-100 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4 text-gray-400 hover:text-red-600" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination. Always shown when there is anything at all — the row
            count is the honest answer to "is that everything?", which the old
            "Showing 100 of 100" was not. */}
        {!loading && total > 0 && (
          <div className="mt-6 flex flex-wrap items-center justify-between gap-3
                          rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm">
            <span className="text-gray-600">
              Showing <span className="font-semibold text-gray-900">{firstRow}–{lastRow}</span>
              {' '}of <span className="font-semibold text-gray-900">{total}</span>
              {' '}notification{total !== 1 ? 's' : ''}
            </span>

            <div className="flex flex-wrap items-center gap-2">
              <label className="flex items-center gap-2 text-gray-600">
                Per page
                <select
                  value={pageSize}
                  onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                  className="rounded-lg border border-gray-300 px-2 py-1 focus:outline-none
                             focus:ring-2 focus:ring-blue-500"
                  data-testid="notifications-page-size"
                >
                  {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </label>

              <div className="flex items-center gap-1">
                <button type="button" onClick={() => setPage(1)} disabled={page === 1}
                  className={pagerBtn} data-testid="notifications-first">« First</button>
                <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1} className={pagerBtn} data-testid="notifications-prev">‹ Prev</button>
                <span className="px-3 text-gray-600" data-testid="notifications-page">
                  Page <span className="font-semibold text-gray-900">{page}</span> / {totalPages}
                </span>
                <button type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages} className={pagerBtn} data-testid="notifications-next">Next ›</button>
                <button type="button" onClick={() => setPage(totalPages)} disabled={page >= totalPages}
                  className={pagerBtn} data-testid="notifications-last">Last »</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
      </div>
  );
};

export default Notifications;
